import { Alert, Anchor, Badge, Loader, Stack, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { sporocilo } from '../api/napaka';
import { api, podatki } from '../api/odjemalec';
import { casIzMs, denar, stevilo } from '../oblika/oblika';
import { Kartica } from '../postavitev/Podatki';
import { STATUSI, type StatusNaloga } from './delavnica';

/**
 * Delavnica na zaslonu stranke (kupca): njena vozila in delovni nalogi (najnovejši najprej, do 50), z obema
 * povezavama na servisno knjigo vozila in nalog.
 */
export function DelavnicaOsebe({ kupecId }: { kupecId: number }) {
  const vozila = useQuery({
    queryKey: ['vozila-strank', 'kupec', kupecId],
    queryFn: ({ signal }) =>
      podatki(
        api.GET('/api/delavnica/vozila/pregled', { signal, params: { query: { kupec: kupecId, velikost: 50 } } }),
      ),
  });
  const nalogi = useQuery({
    queryKey: ['delovni-nalogi', 'kupec', kupecId],
    queryFn: ({ signal }) =>
      podatki(
        api.GET('/api/delavnica/nalogi/pregled', { signal, params: { query: { kupec: kupecId, velikost: 50 } } }),
      ),
  });
  const napaka = vozila.error ?? nalogi.error;
  return (
    <Stack gap="md" mt="md">
      {napaka && (
        <Alert color="red" variant="light" role="alert">
          {sporocilo(napaka)}
        </Alert>
      )}
      <Kartica naslov={`Vozila v delavnici${vozila.data ? ` (${vozila.data.skupaj})` : ''}`}>
        {!vozila.data ? (
          <Loader size="sm" />
        ) : (vozila.data.vsebina ?? []).length === 0 ? (
          <Text size="sm" c="dimmed">
            Stranka nima vozil v delavnici.
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={420}>
            <Table>
              <Table.Tbody>
                {(vozila.data.vsebina ?? []).map((v) => (
                  <Table.Tr key={v.id}>
                    <Table.Td>
                      <Anchor component={Link} to={`/delavnica/vozila/${v.id}`} fw={600}>
                        {v.registrska || v.vin}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>{[v.znamka, v.model].filter(Boolean).join(' ')}</Table.Td>
                    <Table.Td>{v.letnik}</Table.Td>
                    <Table.Td ta="right" className="brez-preloma">
                      {v.km != null ? `${stevilo(v.km)} km` : ''}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Kartica>
      <Kartica naslov={`Delovni nalogi${nalogi.data ? ` (${nalogi.data.skupaj})` : ''}`}>
        {!nalogi.data ? (
          <Loader size="sm" />
        ) : (nalogi.data.vsebina ?? []).length === 0 ? (
          <Text size="sm" c="dimmed">
            Stranka nima delovnih nalogov.
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={520}>
            <Table>
              <Table.Tbody>
                {(nalogi.data.vsebina ?? []).map((n) => (
                  <Table.Tr key={n.id}>
                    <Table.Td>
                      <Anchor component={Link} to={`/delavnica/nalogi/${n.id}`} fw={600} className="brez-preloma">
                        {n.stevilka}
                      </Anchor>
                    </Table.Td>
                    <Table.Td className="brez-preloma">{casIzMs(n.sprejeto)}</Table.Td>
                    <Table.Td>{n.registrska}</Table.Td>
                    <Table.Td>
                      {n.status && (
                        <Badge color={STATUSI[n.status as StatusNaloga].barva} variant="light">
                          {STATUSI[n.status as StatusNaloga].naziv}
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td ta="right" className="brez-preloma">
                      {denar(n.skupaj)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Kartica>
    </Stack>
  );
}
